/**
 * <cookie-consent>
 * ---------------------------------------------------------------------------
 * Banner de consentimiento apoyado en la Customer Privacy API de Shopify.
 *
 * Por qué la API de Shopify y no una cookie propia:
 *   `window.Shopify.customerPrivacy.setTrackingConsent()` es lo único que
 *   propaga la decisión a Shopify Analytics, a los píxeles del cliente y a
 *   Google Consent Mode v2. Guardar una cookie por nuestra cuenta dejaría los
 *   píxeles disparando igual — que es justo lo que la ley prohíbe.
 *
 * El banner solo aparece si Shopify dice que hace falta pedir consentimiento
 * (`shouldShowBanner()`), es decir, en las regiones donde es obligatorio y
 * mientras el visitante no haya decidido todavía.
 */

class CookieConsent extends HTMLElement {
  connectedCallback() {
    this.options = this.querySelector('[data-consent-options]');

    this.querySelector('[data-consent-accept]')?.addEventListener('click', () =>
      this.#save({ analytics: true, marketing: true, preferences: true })
    );

    this.querySelector('[data-consent-reject]')?.addEventListener('click', () =>
      this.#save({ analytics: false, marketing: false, preferences: false })
    );

    // La "X" cierra con la misma decisión que "Rechazar todo": si solo
    // ocultara el banner sin registrar nada, `shouldShowBanner()` seguiría
    // devolviendo true y el aviso volvería a aparecer en cada página.
    this.querySelector('[data-consent-dismiss]')?.addEventListener('click', () =>
      this.#save({ analytics: false, marketing: false, preferences: false })
    );

    this.querySelector('[data-consent-customize]')?.addEventListener('click', () => {
      if (this.options) this.options.hidden = !this.options.hidden;
    });

    this.querySelector('[data-consent-save]')?.addEventListener('click', () => {
      const consent = {};
      this.querySelectorAll('[data-consent-category]').forEach((input) => {
        consent[input.dataset.consentCategory] = input.checked;
      });
      this.#save(consent);
    });

    this.#waitForPrivacyApi().then((api) => {
      if (!api) return;
      // Solo se muestra donde la ley lo exige y si aún no hay decisión tomada
      if (api.shouldShowBanner()) this.hidden = false;
    });
  }

  /**
   * La Customer Privacy API la carga Shopify de forma asíncrona: hay que
   * esperarla en lugar de asumir que ya está.
   */
  #waitForPrivacyApi(timeout = 5000) {
    return new Promise((resolve) => {
      const existing = window.Shopify?.customerPrivacy;
      if (existing) return resolve(existing);

      const start = Date.now();
      const poll = setInterval(() => {
        const api = window.Shopify?.customerPrivacy;
        if (api) {
          clearInterval(poll);
          resolve(api);
        } else if (Date.now() - start > timeout) {
          clearInterval(poll);
          console.warn('[cookie-consent] La Customer Privacy API no está disponible.');
          resolve(null);
        }
      }, 100);
    });
  }

  #save(consent) {
    const api = window.Shopify?.customerPrivacy;
    if (!api) return;

    api.setTrackingConsent(
      {
        analytics: Boolean(consent.analytics),
        marketing: Boolean(consent.marketing),
        preferences: Boolean(consent.preferences),
        sale_of_data: Boolean(consent.marketing),
      },
      () => {
        this.hidden = true;
        document.dispatchEvent(
          new CustomEvent('consent:updated', { bubbles: true, detail: consent })
        );
      }
    );
  }
}

if (!customElements.get('cookie-consent')) {
  customElements.define('cookie-consent', CookieConsent);
}
